import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Star } from "lucide-react";
import { formatBHD } from "@/lib/utils";
import { WishlistButton } from "./WishlistButton";
import { Link } from "react-router-dom";

interface ProductCardProps {
  id: string;
  name: string;
  price: number;
  sale_price?: number; // Primary field for sale prices from database
  originalPrice?: number; // Keep for backward compatibility
  image?: string; // Keep for backward compatibility
  images?: Array<{ url: string; position: number }>; // New images array support
  category?: string;
  badge?: string;
  rating?: number;
}

const ProductCard = ({
  id,
  name,
  price,
  sale_price,
  originalPrice,
  image,
  images,
  category,
  badge,
  rating = 4.5,
}: ProductCardProps) => {
  // Get primary image - support both old (single image) and new (images array) formats
  const primaryImage = (() => {
    if (images && images.length > 0) {
      return images[0].url;
    }
    if (image) {
      return image;
    }
    return '/placeholder-product.png';
  })();

  // Calculate discount percentage - prefer sale_price over originalPrice
  const actualSalePrice = sale_price || (originalPrice && originalPrice > price ? price : null);
  const actualRegularPrice = sale_price ? price : originalPrice;

  const discountPercent = actualSalePrice && actualRegularPrice && actualRegularPrice > actualSalePrice
    ? Math.round(((actualRegularPrice - actualSalePrice) / actualRegularPrice) * 100)
    : 0;

  // Show sale badge if there's a discount
  const displayBadge = discountPercent > 0
    ? `${discountPercent}% OFF`
    : badge;

  return (
    <Card className="group overflow-hidden hover:shadow-2xl hover:shadow-primary/20 transition-all duration-300 bg-card border-border">
      <Link to={`/product/${id}`}>
        <div className="relative overflow-hidden bg-muted">
          <img
            src={primaryImage}
            alt={name}
            className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
            onError={(e) => {
              // Fallback if image fails to load
              e.currentTarget.src = '/placeholder-product.png';
            }}
          />

          {/* Overlay Actions */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="absolute bottom-4 left-4 right-4 flex gap-2">
              <Button className="flex-1 bg-primary hover:bg-primary/90" size="sm">
                <ShoppingCart className="h-4 w-4 mr-2" />
                Add to Cart
              </Button>
            </div>
          </div>

          {/* Top Badges */}
          <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
            {displayBadge && (
              <Badge className={discountPercent > 0 ? "bg-red-500 hover:bg-red-600 text-white" : "bg-secondary hover:bg-secondary text-secondary-foreground"}>
                {displayBadge}
              </Badge>
            )}
            <WishlistButton
              productId={id}
              className="ml-auto"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          </div>
        </div>

        <CardContent className="p-4">
          {category && (
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
              {category}
            </div>
          )}
          <h3 className="font-semibold text-base mb-2 line-clamp-2 group-hover:text-primary transition-colors">
            {name}
          </h3>

          <div className="flex items-center gap-1 mb-2">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`h-4 w-4 ${
                  i < Math.floor(rating) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
                }`}
              />
            ))}
            <span className="text-xs text-muted-foreground ml-1">({rating})</span>
          </div>
        </CardContent>

        <CardFooter className="p-4 pt-0 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            {actualSalePrice && actualRegularPrice && actualSalePrice < actualRegularPrice ? (
              <>
                <span className="text-xl font-bold text-primary">{formatBHD(actualSalePrice)}</span>
                <span className="text-sm text-muted-foreground line-through">
                  {formatBHD(actualRegularPrice)}
                </span>
              </>
            ) : (
              <span className="text-xl font-bold">{formatBHD(price)}</span>
            )}
          </div>

          {discountPercent > 0 && (
            <Badge variant="destructive" className="text-xs">
              -{discountPercent}%
            </Badge>
          )}
        </CardFooter>
      </Link>
    </Card>
  );
};

export default ProductCard;
